import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { API } from "../lib/api";
import { useAuth } from "./AuthContext";

const DEFAULT_APP_SETTINGS = {
  applicationVersion: "V26.1.0",
  forecastMargins: {
    Buyout: 23,
    Components: 45,
    Equipment: 45,
    Installation: 28,
    Service: 50,
    Dryers: 30,
    "Spare Parts": 30
  },
  defaultMarginPercentage: 30,
  maxUsers: null,
  apiConnectors: [],
};

export const getForecastMarginEntries = (settings = DEFAULT_APP_SETTINGS) => {
  const configuredMargins = settings?.forecastMargins;
  const source =
    configuredMargins === undefined
      ? DEFAULT_APP_SETTINGS.forecastMargins
      : configuredMargins;

  return Object.entries(source);
};

export const getForecastMarginProductGroups = (settings = DEFAULT_APP_SETTINGS) =>
  getForecastMarginEntries(settings).map(([groupName]) => groupName);

const AppSettingsContext = createContext({
  applicationSettings: DEFAULT_APP_SETTINGS,
  refreshApplicationSettings: async () => DEFAULT_APP_SETTINGS,
  setApplicationSettings: () => {},
});

const mergeApplicationSettings = (settings = {}) => ({
  ...DEFAULT_APP_SETTINGS,
  ...settings,
  forecastMargins:
    settings.forecastMargins === undefined
      ? DEFAULT_APP_SETTINGS.forecastMargins
      : settings.forecastMargins,
  apiConnectors: Array.isArray(settings.apiConnectors)
    ? settings.apiConnectors
    : DEFAULT_APP_SETTINGS.apiConnectors,
});

export const AppSettingsProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [applicationSettings, setApplicationSettingsState] = useState(DEFAULT_APP_SETTINGS);

  const refreshApplicationSettings = useCallback(async () => {
    const isAdmin = (user?.userId || user?.id) && user?.role === "admin";
    let nextSettings = DEFAULT_APP_SETTINGS;

    if (isAdmin) {
      const fullSettings = await API.Settings.FetchApplication();

      if (fullSettings && Object.keys(fullSettings).length) {
        nextSettings = mergeApplicationSettings(fullSettings);
      } else {
        const publicSettings = await API.Settings.FetchApplicationPublic();
        nextSettings = mergeApplicationSettings(publicSettings);
      }
    } else {
      const publicSettings = await API.Settings.FetchApplicationPublic();
      nextSettings = mergeApplicationSettings(publicSettings);
    }

    setApplicationSettingsState(nextSettings);
    return nextSettings;
  }, [user?.id, user?.userId, user?.role]);

  useEffect(() => {
    if (loading) {
      return;
    }

    refreshApplicationSettings();
  }, [loading, refreshApplicationSettings]);

  const setApplicationSettings = (settings) => {
    setApplicationSettingsState(mergeApplicationSettings(settings));
  };

  return (
    <AppSettingsContext.Provider
      value={{
        applicationSettings,
        refreshApplicationSettings,
        setApplicationSettings,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => useContext(AppSettingsContext);
